import styles from '../index.module.css'

import { IoArrowDown } from '@react-icons/all-files/io5/IoArrowDown'
import { IoArrowUp } from '@react-icons/all-files/io5/IoArrowUp'
import { LuGripVertical } from '@react-icons/all-files/lu/LuGripVertical'
import { Column, flexRender, Header } from '@tanstack/react-table'
import cn from 'clsx'
import React, { CSSProperties, useCallback, useRef } from 'react'
import { useDrag, useDrop } from 'react-dnd'

import * as ItemTypes from '@disco/constants/ItemTypes'

import { Flex, Table as RadixTable } from 'components/ds'

import { SearchTableColumnDropdownMenu } from '../table-column-dropdown'
import { sanitizeColumnId } from '../table-utils'

const getHeaderPinningStyles = <T,>(column: Column<T>): CSSProperties => {
  const isPinned = column.getIsPinned()

  return {
    width: `calc(var(--header-${sanitizeColumnId(column.id)}-size) * 1px)`,
    left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
    right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
  }
}

type DraggableHeaderCellProps<T> = {
  header: Header<T, unknown>
  onColumnReorderDrop: (_draggedId: string, _targetId: string) => void
  isResizingRef: React.MutableRefObject<boolean>
  setIsResizing: (_value: boolean) => void
  onColumnVisibilityChange?: (_v: unknown) => void
  leafColumns: Column<T>[]
}

const DraggableHeaderCell = <T,>({
  header,
  onColumnReorderDrop,
  isResizingRef,
  setIsResizing,
  onColumnVisibilityChange,
  leafColumns,
}: DraggableHeaderCellProps<T>) => {
  const ref = useRef<HTMLTableCellElement>(null)
  // meta is augmented in frontend's tanstack-table.d.ts, which the cl strict program
  // doesn't compile; read it via its explicit shape so both compile
  const columnMeta = header.column.columnDef.meta as
    | { customSortUI?: boolean; enableDragging?: boolean }
    | undefined
  const isDraggable =
    columnMeta?.enableDragging !== false && !header.column.getIsPinned()

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.TABLE_COLUMN,
    item: { id: header.id },
    canDrag: () => isDraggable && !isResizingRef.current,
    collect: monitor => ({ isDragging: monitor.isDragging() }),
  })

  const [{ isOver }, drop] = useDrop({
    accept: ItemTypes.TABLE_COLUMN,
    canDrop: () => isDraggable,
    drop: (item: { id: string }) => {
      if (item.id !== header.id) {
        onColumnReorderDrop(item.id, header.id)
      }
    },
    collect: monitor => ({ isOver: monitor.isOver() && monitor.canDrop() }),
  })

  // always attach connectors; canDrag/canDrop gate at runtime so pin state
  // changes after mount take effect without stale connector registration
  drag(drop(ref))

  return (
    <RadixTable.ColumnHeaderCell
      ref={ref}
      key={header.id}
      align={header.id === 'actions' ? 'right' : undefined}
      /* eslint-disable-next-line react/forbid-component-props -- dynamic column pin offsets require an inline style */
      style={getHeaderPinningStyles(header.column)}
      width={`calc(var(--header-${sanitizeColumnId(
        header.column.id,
      )}-size) * 1px)`}
      className={cn(styles.HeaderContent, {
        [styles.PinnedCell]: header.column.getIsPinned(),
        [styles.ActionsCell]: header.id === 'actions',
        [styles.DragOverHeader]: isOver,
        [styles.Draggable]: isDraggable,
        [styles.Dragging]: isDragging,
      })}
    >
      <Flex height="100%" justify="between" align="center" width="100%">
        <Flex
          gap="1"
          align="center"
          className={cn({
            [styles.SortableHeader]:
              header.column.getCanSort() && !isResizingRef.current,
          })}
          onClick={
            header.column.getCanSort() && !isResizingRef.current
              ? header.column.getToggleSortingHandler()
              : undefined
          }
        >
          {isDraggable && (
            <span className={styles.DragHandle}>
              <LuGripVertical />
            </span>
          )}
          {header.isPlaceholder
            ? null
            : flexRender(header.column.columnDef.header, header.getContext())}
          <Flex>
            {header.column.getCanSort() &&
              !columnMeta?.customSortUI &&
              { asc: <IoArrowDown />, desc: <IoArrowUp /> }[
                header.column.getIsSorted() as string
              ]}
          </Flex>
        </Flex>

        {header.id === 'actions' && onColumnVisibilityChange && (
          <SearchTableColumnDropdownMenu columns={leafColumns} />
        )}

        <Flex
          {...(header.column.getCanResize()
            ? {
                onDoubleClick: () => header.column.resetSize(),
                onMouseDown: e => {
                  e.stopPropagation()
                  setIsResizing(true)
                  header.getResizeHandler()(e)
                },
                onTouchStart: e => {
                  e.stopPropagation()
                  setIsResizing(true)
                  header.getResizeHandler()(e)
                },
                onMouseUp: () => {
                  setTimeout(() => setIsResizing(false), 150)
                },
                onTouchEnd: () => {
                  setTimeout(() => setIsResizing(false), 150)
                },
                onMouseLeave: () => {
                  setTimeout(() => setIsResizing(false), 150)
                },
                className: `${cn({
                  [styles.Resizer]: header.column.getCanResize(),
                  [styles.Resizing]: header.column.getIsResizing(),
                })}`,
              }
            : {})}
        />
      </Flex>
    </RadixTable.ColumnHeaderCell>
  )
}

export const TableHeader: React.FC<{
  table
  stickyHeader
  onColumnVisibilityChange
  leafColumns
  onColumnReorderDrop: (_draggedId: string, _targetId: string) => void
}> = ({
  table,
  stickyHeader,
  onColumnVisibilityChange,
  leafColumns,
  onColumnReorderDrop,
}) => {
  const isResizingRef = useRef(false)
  const setIsResizing = useCallback((value: boolean) => {
    isResizingRef.current = value
  }, [])

  return (
    <RadixTable.Header
      className={cn({ [styles.StickyTableHeader]: stickyHeader })}
    >
      {table.getHeaderGroups().map(headerGroup => (
        <RadixTable.Row key={headerGroup.id}>
          {headerGroup.headers.map(header => (
            <DraggableHeaderCell
              key={header.id}
              header={header}
              onColumnReorderDrop={onColumnReorderDrop}
              isResizingRef={isResizingRef}
              setIsResizing={setIsResizing}
              onColumnVisibilityChange={onColumnVisibilityChange}
              leafColumns={leafColumns}
            />
          ))}
        </RadixTable.Row>
      ))}
    </RadixTable.Header>
  )
}
